import React, {FC, ReactNode} from 'react';
import classNames from 'classnames';
import DynamicAsset from 'components/DynamicAsset';

import {Circles} from './Circles';

import * as styles from './styles.module.scss';
import * as wrapStyles from 'pages/dashboard/styles.module.scss';

interface BG {
	color?: 'blue' | 'green';
	image?: string;
	component?: ReactNode;
}

interface Card {
	className?: string;
	children: ReactNode;

	bg?: BG;
	circles?: boolean;
	shadow?: boolean;
	sz?: 'double' | 'triple';

	onClick?: () => void;
}

const useBackground = (bg?: BG): [string, ReactNode?] => {
	if (!bg) {
		return ['', null];
	} else if (bg.component) {
		return [styles.noBorder, bg.component];
	}

	const colorStyle = (bg: BG) => {
		switch (bg.color) {
		case 'blue':
			return styles.blue;
		case 'green':
			return styles.green;
		default:
			return '';
		}
	};

	// Color only
	if (!bg.image && bg.color) {
		return [colorStyle(bg), null];
	}

	// Full image
	if (!bg.color && bg.image) {
		return [
			styles.noBorder,
			<DynamicAsset
				className={styles.image}
				imageSrc={bg.image}
				key={bg.image}
			/>
		];
	}

	// Half color, half image
	if (bg.color && bg.image) {
		return [
			classNames(
				colorStyle(bg),
				styles.partial,
			),
			<DynamicAsset
				className={styles.image}
				imageSrc={bg.image}
				key={bg.image}
			/>
		];
	}

	// Throw error; must set at least one value
	throw new Error('Set image, color, or both');
};

const Card: FC<Card> = ({className, children, onClick, ...rest}) => {
	const [bgStyle, bgComponent] = useBackground(rest.bg);
	const props = {
		className: classNames({
			[styles.wrapper]: true,
			[styles.click]: Boolean(onClick),
			[bgStyle]: Boolean(bgStyle),
			[styles.shadow]: rest.shadow,

			[wrapStyles.double]: rest.sz === 'double',
			[wrapStyles.triple]: rest.sz === 'triple',
		}),
		onClick,
	};

	return (
		<div {...props}>
			<div className={classNames(styles.inner, className)}>
				{children}
			</div>
			{bgComponent}
			{rest.circles && <Circles />}
		</div>
	);
};

export default Card;
